import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '../../context/SessionContext';
import { useMusicKit } from '../../context/MusicKitContext';
import { usePlaylistMusic } from '../../context/PlaylistMusicContext';
import PersistentMiniPlayer from './PersistentMiniPlayer';
import PlaylistSoundBar from './PlaylistSoundBar';
import SimpleSongBar from './SimpleSongBar';
import AppleMusicAlreadyLinkedModal from './AppleMusicAlreadyLinkedModal';

/**
 * AppLayout - Wrapper component that includes song bars based on context
 * 
 * THREE types of song bars:
 * 
 * 1. PersistentMiniPlayer (Session - Full Bar):
 *    - Shows on home screen when user is IN an active session but NOT viewing it
 *    - Has fire emoji + fingerprint
 * 
 * 2. PlaylistSoundBar (Playlist - Full Bar):
 *    - Shows on playlist pages (MyPlaylists, Playlist Detail) and when playing playlist music
 *    - Has fire emoji + fingerprint (same restrictions as session bar for guests)
 * 
 * 3. SimpleSongBar (Standalone - Simple Bar):
 *    - Shows when playing music from home search, DMs, etc.
 *    - NO fire emoji, NO fingerprint - just basic controls
 * 
 * CRITICAL: NO song bar should EVER show on session screens!
 * Sessions have their own playback controls built in.
 */
const AppLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeSessionData, currentSession, isInActiveSession } = useSession();
  const { appleMusicError, linkedUsername, clearAppleMusicError, currentSong: musicKitCurrentSong, musicSource, setMusicSource, stop: stopMusicKit } = useMusicKit();
  const { hasPlaylist, currentPlaylistId, currentTrack: playlistCurrentTrack, isPlaylistPlaying, stopPlaylist } = usePlaylistMusic();
  
  // Path checks - define early so it can be used in useRef and useEffect
  const currentPath = location.pathname;
  
  // Track previous path to detect navigation away from playlists/sessions
  const prevPathRef = useRef(currentPath);
  
  // Clear old playlist queue when navigating away from playlist pages
  useEffect(() => {
    const prevPath = prevPathRef.current;
    prevPathRef.current = currentPath;
    
    const wasOnPlaylist = prevPath.startsWith('/app/playlist/');
    const isOnPlaylist = currentPath.startsWith('/app/playlist/');
    
    // If user navigated away from a playlist detail page to a non-playlist page
    if (wasOnPlaylist && !isOnPlaylist && stopPlaylist) {
      stopPlaylist();
    }
  }, [currentPath, stopPlaylist]);
  const isHomePage = currentPath === '/app';
  const isMyPlaylistsPage = currentPath === '/app/myplaylists';
  const isPlaylistDetailPage = currentPath.startsWith('/app/playlist/');
  const isEventPage = currentPath.startsWith('/event/');
  const hasSessionParam = location.search.includes('session=');
  const isSessionPath = currentPath.includes('/session');
  const isViewingSession = currentSession !== null;
  
  // CRITICAL: Check if we're on ANY session-related screen
  // NO SONG BARS should show here - sessions have their own controls!
  const isOnSessionScreen = hasSessionParam || isSessionPath || isViewingSession;
  
  // Check if user is playing standalone music (search/DM) which should override session mini player
  const isPlayingStandaloneMusic = musicKitCurrentSong && (musicSource === 'search' || musicSource === 'dm');
  
  // Session Mini Player: Show on home when in active session but NOT viewing the session
  // AND not when user is playing standalone music (search/DM takes priority)
  const showSessionMiniPlayer = isHomePage && !hasSessionParam && !isSessionPath && !isViewingSession && isInActiveSession && !isPlayingStandaloneMusic;
  
  // Determine if we're on a playlist-related page
  const isOnPlaylistPage = isMyPlaylistsPage || isPlaylistDetailPage;
  
  // Determine if music is from playlist context
  const isMusicFromPlaylist = (hasPlaylist && (playlistCurrentTrack || currentPlaylistId)) || musicSource === 'playlist' || isPlaylistPlaying;
  
  // Playlist Sound Bar (Full Song Bar): Show on any playlist page or when playlist music is playing
  // NEVER show on session screens or event pages
  // NEVER show when music is explicitly from search/DM (SimpleSongBar handles those)
  const hasActivePlaylist = hasPlaylist || currentPlaylistId || isPlaylistPlaying;
  const showPlaylistSoundBar = (isOnPlaylistPage || isMusicFromPlaylist || hasActivePlaylist) && !isOnSessionScreen && !isEventPage && !isInActiveSession && musicSource !== 'search' && musicSource !== 'dm';
  
  // Simple Song Bar: Show when MusicKit is playing standalone music (not from session/playlist)
  // This handles home search, DM music, etc.
  // NEVER show on session screens, event pages, or playlist detail pages
  const isStandaloneMusic = musicKitCurrentSong && (musicSource === 'search' || musicSource === 'dm' || (!musicSource && !isMusicFromPlaylist && !isInActiveSession));
  const showSimpleSongBar = isStandaloneMusic && !isOnSessionScreen && !isEventPage && !isPlaylistDetailPage && !showSessionMiniPlayer;
  
  // Handler to close simple song bar
  const handleCloseSimpleSongBar = () => {
    if (stopMusicKit) {
      stopMusicKit();
    }
    if (setMusicSource) {
      setMusicSource(null);
    }
  };
  
  // Navigate to session when mini player is tapped
  const handleMiniPlayerTap = () => {
    if (activeSessionData?.id) {
      navigate(`/app?session=${activeSessionData.id}`);
    }
  };

  return (
    <>
      {children}
      
      {/* Session Mini Player - Shows when in active session on home (but not viewing session) */}
      {showSessionMiniPlayer && (
        <PersistentMiniPlayer onTap={handleMiniPlayerTap} />
      )}
      
      {/* Playlist Sound Bar - Shows ONLY when music is from playlist context */}
      {showPlaylistSoundBar && (
        <PlaylistSoundBar />
      )}
      
      {/* Simple Song Bar - Shows for standalone music (home search, DMs) */}
      {/* NO fire emoji, NO fingerprint - just basic playback controls */}
      {showSimpleSongBar && (
        <SimpleSongBar onClose={handleCloseSimpleSongBar} />
      )}
      
      {/* Apple Music Already Linked Modal - STRICT POLICY: 1 Apple Music = 1 Profile */}
      <AppleMusicAlreadyLinkedModal
        isOpen={appleMusicError === 'already_linked'}
        onClose={clearAppleMusicError}
        linkedUsername={linkedUsername || 'unknown'}
      />
    </>
  );
};

export default AppLayout;
